import React from 'react';
import PropTypes from 'prop-types';
import {Label} from './Label';
import {Wrapper} from './Wrapper';
import {MoveIcon} from './MoveIcon';
import {Feedback} from '../Feedback';
import {IconBlock} from './IconBlock';
import {LabelBlock} from './LabelBlock';
import {Utilities} from '@learningblocks/exp-constants';
const {DRAG_DROP__ID} = Utilities;
import './style.scss';
import {Empty} from './Empty';

/**
 * EmperimentStep
 *  - Used in DesignViewThree and DesignViewTwo;
 *    Display the Title of ExperimentStep
 */
export function ExperimentStep(props) {
  const onSelect = () => props.onSelect && props.onSelect();

  const onRemove = () => !props.isCorrect && props.onRemove && props.onRemove();

  const renderOption = () => (
    <Wrapper tabIndex={props.tabIndex} className='expStepOption'>
      <IconBlock
        tabIndex={props.tabIndex}
        className='expStepOption_icon'
        onSelect={onSelect}
        color={props.color}
        ariaLabel={`\"${props.text}\" move button.`}
      >
        <MoveIcon color={props.color} className='expStepOption_icon--icon' />
      </IconBlock>
      <LabelBlock className='expStepOption_label'>
        <Label text={props.text} className='expStepOption_label--label' />
      </LabelBlock>
    </Wrapper>
  );

  const renderStepCorrect = () => (
    <Wrapper tabIndex={props.tabIndex} className='expStep'>
      <IconBlock
        tabIndex={-1}
        className='expStep_icon'
        onSelect={onSelect}
        color={props.color}>
      </IconBlock>
      <LabelBlock className='expStep_label'>
        <Label text={props.text} className='expStep_label--label' />
      </LabelBlock>
      <Feedback.Wrap
        onSelect={onRemove}
        tabIndex={-1}
        className='expStep_feedback'>
        <Feedback.IconCorrect className='expStep_feedback--icon' />
      </Feedback.Wrap>
    </Wrapper>
  );

  const renderStepIncorrect = () => (
    <Wrapper tabIndex={props.tabIndex} className='expStep'>
      <IconBlock
        tabIndex={-1}
        className='expStep_icon'
        onSelect={onSelect}
        color={props.color}>
      </IconBlock>
      <LabelBlock className='expStep_label'>
        <Label text={props.text} className='expStep_label--label' />
      </LabelBlock>
      <Feedback.Wrap
        onSelect={onRemove}
        tabIndex={props.tabIndex}
        className='expStep_feedback'>
        <Feedback.IconIncorrect
          className='expStep_feedback--icon'
          show={props.showTooltip}
          message={props.feedbackMessage}
        />
      </Feedback.Wrap>
    </Wrapper>
  );

  const onDrop = React.useCallback((event) => {
    const optionId = parseInt(event.dataTransfer.getData(DRAG_DROP__ID));
    props.onDrop({optionId});
  }, []);

  const renderEmptyStep = () => (
    <Empty
      onDrop={onDrop}
      onAddStep={props.onDrop}
      tabIndex={props.tabIndex}
      placeHolder={props.placeHolder}
      options={props.optionsList}
    />
  );

  if (props.onSelect) {
    return renderOption();
  } else {
    if (props.isEmpty) {
      return renderEmptyStep();
    } else {
      if (props.isCorrect) {
        return renderStepCorrect();
      } else {
        return renderStepIncorrect();
      }
    }
  }
}

ExperimentStep.propTypes = {
  /**
   * tabIndex to set on element and its children
   */
  tabIndex: PropTypes.number,
  /**
   * Main title for the step/option
   */
  text: PropTypes.string,
  /**
   * placeHolder to display when step is emtpy
   */
  placeHolder: PropTypes.string,
  /**
   * Theme color of the option
   */
  color: PropTypes.string,
  /**
   * feedback message for the step
   */
  feedbackMessage: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  /**
   * Is the Step Empty
   */
  isEmpty: PropTypes.bool.isRequired,
  /**
   * Is the Option placed correct or incorrect
   */
  isCorrect: PropTypes.bool,
  /**
   * Function to trigger remove option from step action when wrongly dropped
   * Triggered on click of Incorrect-Icon
   */
  onRemove: PropTypes.func,
  /**
   * Function to trigger add as next step action ( when used as option )
   * Triggered on click of MoveIcon
   */
  onSelect: PropTypes.func,
  /**
   * Function involked when an option is dropped
   * on EmptyNodeHolder
   */
  onDrop: PropTypes.func,
  /**
   * list of step options for modal window in mobile view
   */
  optionsList: PropTypes.array,
  /**
   * for keyboard navigation
   */
  tabIndex: PropTypes.number,
};

ExperimentStep.defaultProps = {
  color: '#003e6b',
  onRemove: undefined,
  onSelect: undefined,
};
